import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '../hooks/useQuery';
import { api, type SalesQuotaRow } from '../lib/api';
import { Card, CardHeader, Table, Th, Tr, Td, Badge, Spinner, EmptyState, Select, ActionButton } from '../components/ui';
import { fmtCurrencyWhole } from '../lib/fmt';

const hdr: React.CSSProperties = { fontSize: 13, fontWeight: 600 };
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const thisYear = new Date().getFullYear();
const YEARS = [thisYear - 1, thisYear, thisYear + 1];

const key = (owner: string, month: string) => `${owner}|${month}`;
const monthOf = (year: number, i: number) => `${year}-${String(i + 1).padStart(2, '0')}`;

const inputStyle: React.CSSProperties = {
  width: 78, textAlign: 'right', fontSize: 12, padding: '3px 5px',
  border: '0.5px solid var(--color-border)', borderRadius: 4,
  background: 'var(--color-surface)', color: 'var(--color-text)',
};

export default function AdminSalesQuotasPage() {
  const [year, setYear] = useState(thisYear);
  const { data, isLoading, error } = useQuery(() => api.sales.quotas(year), [year]);
  // Editable values keyed by owner|month -> raw input string.
  const [vals, setVals] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  // Seed the grid whenever a year's data arrives.
  useEffect(() => {
    if (!data) return;
    const seed: Record<string, string> = {};
    for (const r of data.rows) seed[key(r.hubspot_owner_id, r.period_month)] = String(r.target_amount);
    setVals(seed);
    setSavedMsg(null);
  }, [data]);

  const repTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    if (data) for (const rep of data.reps) {
      let t = 0;
      for (let i = 0; i < 12; i++) t += Number(vals[key(rep.hubspot_owner_id, monthOf(year, i))] || 0);
      totals[rep.hubspot_owner_id] = t;
    }
    return totals;
  }, [vals, data, year]);

  if (isLoading) return <Card><CardHeader><div style={hdr}>Sales targets</div></CardHeader><Spinner /></Card>;
  if (error || !data) return <Card><CardHeader><div style={hdr}>Sales targets</div></CardHeader><EmptyState message={error ?? 'No data'} /></Card>;

  const set = (owner: string, month: string, v: string) => {
    setVals((prev) => ({ ...prev, [key(owner, month)]: v.replace(/[^0-9.]/g, '') }));
    setSavedMsg(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const rows: SalesQuotaRow[] = [];
      for (const rep of data.reps) {
        for (let i = 0; i < 12; i++) {
          const m = monthOf(year, i);
          const raw = vals[key(rep.hubspot_owner_id, m)];
          if (raw == null || raw === '') continue;
          const n = Number(raw);
          if (Number.isFinite(n)) rows.push({ hubspot_owner_id: rep.hubspot_owner_id, period_month: m, target_amount: n });
        }
      }
      const { saved } = await api.sales.saveQuotas(rows);
      setSavedMsg(`Saved ${saved} target${saved === 1 ? '' : 's'}.`);
    } catch (e) {
      setSavedMsg(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div style={hdr}>Sales targets — monthly bookings quota per rep</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Link to="/sales" style={{ fontSize: 12, color: 'var(--color-blue)' }}>← Back to dashboard</Link>
          <Select value={String(year)} onChange={(v) => setYear(Number(v))}>
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </Select>
          <ActionButton variant="primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save targets'}</ActionButton>
        </div>
      </CardHeader>
      {savedMsg && <div style={{ padding: '6px 16px', fontSize: 12, color: 'var(--color-text-muted)' }}>{savedMsg}</div>}
      {data.reps.length === 0 ? <EmptyState message="No sales reps with deals yet." /> : (
        <div style={{ overflowX: 'auto' }}>
          <Table>
            <thead><tr>
              <Th sticky>Rep</Th>
              {MONTHS.map((m) => <Th key={m} right>{m}</Th>)}
              <Th right>Year total</Th>
            </tr></thead>
            <tbody>
              {data.reps.map((rep) => (
                <Tr key={rep.hubspot_owner_id}>
                  <Td bold>
                    {rep.owner_name ?? rep.hubspot_owner_id}
                    {rep.archived && <Badge variant="gray">former</Badge>}
                  </Td>
                  {MONTHS.map((_, i) => {
                    const m = monthOf(year, i);
                    return (
                      <Td key={m} right>
                        <input
                          style={inputStyle}
                          inputMode="decimal"
                          value={vals[key(rep.hubspot_owner_id, m)] ?? ''}
                          onChange={(e) => set(rep.hubspot_owner_id, m, e.target.value)}
                          placeholder="0"
                        />
                      </Td>
                    );
                  })}
                  <Td right bold>{fmtCurrencyWhole(repTotals[rep.hubspot_owner_id] ?? 0)}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Card>
  );
}
